import { Experience } from "../types";

export const experience: Experience[] = [
  {
    id: "niural-backend",
    title: "Software Engineer",
    company: "Niural",
    location: "Lalitpur, Nepal",
    type: "job",
    startDate: "2025-06",
    endDate: "",
    description:
      "Backend engineering role focusing on authentication systems, DevOps infrastructure, and product deployment on AWS cloud services.",
    responsibilities: [
      "Designed and implemented authentication system using AWS Cognito",
      "Built serverless backend infrastructure using Lambda and API Gateway",
      "Managed deployment pipelines with ECS/ECR and CloudFront CDN",
      "Implemented monitoring and logging with CloudWatch",
    ],
    technologies: [
      "Python",
      "AWS Cognito",
      "Lambda",
      "Amplify",
      "CloudWatch",
      "CloudFront",
      "ECS",
      "ECR",
      "DynamoDB",
      "Terraform",
    ],
    achievements: [
      "Reduced authentication latency by 60%",
      "Implemented zero-downtime deployment pipeline",
      "Built scalable serverless architecture",
    ],
  },
  {
    id: "duke-research-intern",
    title: "Research Intern",
    company: "Duke University & IOE Collaboration",
    location: "Remote",
    type: "research",
    startDate: "2024-10",
    endDate: "2025-05",
    description:
      "Research internship under Prof. Dr. Henri Gavin developing deep learning models for earthquake early warning systems. Focused on improving prediction accuracy and reducing false positives in seismic event detection.",
    responsibilities: [
      "Developed neural network architectures optimized for seismic pattern recognition",
      "Implemented data preprocessing pipelines for large-scale seismic datasets",
      "Created feature engineering methods for temporal pattern extraction",
    ],
    technologies: [
      "Python",
      "TensorFlow",
      "PyTorch",
      "NumPy",
      "Pandas",
      "Scikit-learn",
      "Jupyter",
      "Git",
      "Docker",
    ],
    achievements: [
      "Achieved 80% accuracy in earthquake prediction",
      "Published technical report (in preparation)",
    ],
    companyUrl: "https://ktmgeolab.org/",
  },
  {
    id: "lict-research-fellow",
    title: "Research Fellow",
    company: "Laboratory for ICT Research and Development (LICT), IOE",
    location: "Lalitpur, Nepal",
    type: "research",
    startDate: "2024-04",
    endDate: "2025-04",
    description:
      "Research fellowship focusing on 5G vehicular network protocols in collaboration with Universidad Politécnica de Valencia, Spain. Designed and implemented IoT communication protocols for V2X scenarios.",
    responsibilities: [
      "Implemented MQTT and ZeroMQ protocols from scratch in OMNET++",
      "Designed realistic 5G network topologies for vehicular scenarios",
      "Developed performance analysis tools for latency and throughput measurement",
      "Conducted comparative analysis of protocols under various conditions",
    ],
    technologies: [
      "C++",
      "OMNET++",
      "MQTT",
      "ZeroMQ",
      "5G NR",
      "Network Protocols",
      "Simulation Tools",
      "Linux",
      "Python",
    ],
    achievements: [
      "Implemented two major IoT protocols from scratch",
      "International collaboration with UPV, Spain",
    ],
    companyUrl: "http://lict.ioe.tu.edu.np/",
  },
];

// Helper functions
export const getCurrentExperience = (): Experience[] => {
  return experience.filter(
    (exp) => !exp.endDate || new Date(exp.endDate) > new Date()
  );
};

export const getResearchExperience = (): Experience[] => {
  return experience.filter((exp) => exp.type === "research");
};

export const getExperienceById = (id: string): Experience | undefined => {
  return experience.find((exp) => exp.id === id);
};

export const getExperienceDuration = (exp: Experience): string => {
  const start = new Date(exp.startDate);
  const end = exp.endDate ? new Date(exp.endDate) : new Date();

  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffMonths = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 30));

  if (diffMonths < 1) {
    return "< 1 month";
  } else if (diffMonths < 12) {
    return `${diffMonths} month${diffMonths > 1 ? "s" : ""}`;
  } else {
    const years = Math.floor(diffMonths / 12);
    const remainingMonths = diffMonths % 12;
    if (remainingMonths === 0) {
      return `${years} year${years > 1 ? "s" : ""}`;
    } else {
      return `${years} yr${years > 1 ? "s" : ""} ${remainingMonths} mo`;
    }
  }
};

export const getTotalResearchMonths = (): number => {
  return getResearchExperience().reduce((total, exp) => {
    const start = new Date(exp.startDate);
    const end = exp.endDate ? new Date(exp.endDate) : new Date();
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffMonths = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 30));
    return total + diffMonths;
  }, 0);
};

export const getExperienceStats = () => {
  const total = experience.length;
  const research = getResearchExperience().length;
  const current = getCurrentExperience().length;
  const totalResearchMonths = getTotalResearchMonths();

  return {
    total,
    research,
    current,
    totalResearchMonths,
    internationalCollaborations: 2, // Duke + Spain
  };
};